import type { NPaymentConfig } from './types.js';
import { CHAINS } from './chains.js';
import { NPaymentError } from './errors.js';

export function createConfig(input: NPaymentConfig): NPaymentConfig {
  if (!input.chains?.length) {
    throw new NPaymentError('At least one chain required', 'INVALID_CONFIG', 'Pass chains: ["base-sepolia"]');
  }

  for (const chain of input.chains) {
    if (!CHAINS[chain]) {
      throw new NPaymentError(`Unknown chain: ${chain}`, 'INVALID_CONFIG', `Valid chains: ${Object.keys(CHAINS).join(', ')}`);
    }
  }

  if (!input.ows?.wallet) {
    throw new NPaymentError('ows.wallet is required', 'INVALID_CONFIG', 'Pass ows: { wallet: "my-agent" }');
  }

  // Dual-mode: privateKey is optional if OWS SDK is available
  // Validation of signer availability happens at runtime in OWSWallet

  const hasGoatChain = input.chains.some((c) => CHAINS[c].protocols.includes('goat'));
  if (hasGoatChain && !input.goat) {
    throw new NPaymentError('GOAT chains require goat credentials', 'INVALID_CONFIG', 'Pass goat: { apiKey, apiSecret, merchantId }');
  }

  if (input.btcLending && !hasGoatChain) {
    throw new NPaymentError('btcLending requires a GOAT chain', 'INVALID_CONFIG', 'Add goat-testnet or goat-mainnet to chains');
  }

  if (input.btcLending && !input.btcLending.vaultAddress) {
    throw new NPaymentError('btcLending.vaultAddress is required', 'INVALID_CONFIG', 'Pass btcLending: { vaultAddress: "0x..." }');
  }

  if (input.morph?.altFee?.enabled) {
    throw new NPaymentError(
      'AltFee (gas-in-stablecoin) is not yet implemented — planned for v0.10',
      'NOT_IMPLEMENTED',
      'See https://docs.morph.network/docs/morph-rails/infra-solutions/altfee — Type-0x7F transaction support coming next release',
    );
  }

  // v0.11: SpaceRouter requires a creditcoin-* chain when strict.
  const hasSpaceRouterChain = input.chains.some((c) => CHAINS[c].protocols.includes('spacerouter'));
  if (input.spacerouter?.strict && !hasSpaceRouterChain) {
    throw new NPaymentError(
      'spacerouter config requires a creditcoin-* chain',
      'INVALID_CONFIG',
      'Add "creditcoin-mainnet" or "creditcoin-testnet" to chains',
    );
  }

  // v0.13: Aave config validation
  if (input.aave) {
    if (input.aave.maxLTV !== undefined && (input.aave.maxLTV < 1 || input.aave.maxLTV > 90)) {
      throw new NPaymentError('aave.maxLTV must be between 1 and 90', 'INVALID_CONFIG');
    }
    if (input.aave.vault?.feePercent !== undefined && input.aave.vault.feePercent < 10) {
      throw new NPaymentError('aave.vault.feePercent must be >= 10 (Aave requirement)', 'INVALID_CONFIG');
    }
  }

  return { protocol: 'auto', ...input };
}
